import {
  maybePluralize,
  maybePluralizeWord,
} from '@wandb/weave/core/util/string';

import {
  ArtifactPathSearchArtifactsQuery,
  ViewerTeamsProjectsQuery,
} from '../../../../generated/graphql';
import {ParsedPath, PathState, Property} from '../../utilTypes';
import {DOT} from '../utils';
import {ProjectOption} from './types';

export interface ArtifactOption {
  name: string;
  label: string;
  value: string;
  aliasesOrVersions: GroupedAliasOrVersionOptions[];
}

export interface AliasOrVersionOption {
  id: string;
  alias: string;
  label: string;
  value: string;
  type: 'alias' | 'version';
}

export interface GroupedAliasOrVersionOptions {
  label: string;
  options: AliasOrVersionOption[];
}

export function getDisplayName(property: Property) {
  if (property.title) {
    return property.title;
  }
  const keyParts = property.key.split(DOT);
  return keyParts[keyParts.length - 1].replaceAll('\\.', '.');
}

export function parseArtifactPath(input: string): ParsedPath {
  const trimmed = input.trim();
  const parts = trimmed.split('/');
  const trailingSep = trimmed.endsWith(':')
    ? ':'
    : trimmed.endsWith('/')
      ? '/'
      : null;

  const entity = parts[0] || '';
  const project = parts[1] || '';
  let artifact = '';
  let alias = '';

  if (parts[2]) {
    const [artifactName = '', artifactAlias = ''] = parts[2].split(':', 2);
    artifact = artifactName;
    alias = artifactAlias;
  }

  return {entity, project, artifact, alias, trailingSep};
}

export function computeState(input: string): PathState {
  const trimmed = input.trim();
  const colonSplit = trimmed.split(':');

  if (colonSplit.length > 2) {
    return 'INVALID';
  }

  if (colonSplit.length === 2 && !trimmed.endsWith(':')) {
    const parts = trimmed.split('/');
    if (parts.length >= 3) {
      return 'ALIAS';
    }
    return 'INVALID';
  } else if (colonSplit.length > 1 && trimmed.endsWith(':')) {
    return 'ALIAS';
  }

  const parts = trimmed.split('/');

  if (parts.length === 1) {
    return 'ENTITY';
  } else if (parts.length === 2) {
    return 'PROJECT';
  } else if (parts.length === 3) {
    return 'ARTIFACT';
  }

  return 'INVALID';
}

export function getConstraintText(property: Property) {
  switch (property.type) {
    case 'integer':
    case 'number':
      return getNumericalConstraintText(property);
    case 'array':
      return getArrayConstraintText(property);
    default:
      return '';
  }
}

export function getNumericalConstraintText(property: Property) {
  let resp = '';

  if (property.type === 'integer') {
    resp += 'Must be an integer. ';
  }

  const min = property.minimum ?? property.exclusiveMinimum;
  const max = property.maximum ?? property.exclusiveMaximum;
  const minOp = property.exclusiveMinimum != null ? '>' : '≥';
  const maxOp = property.exclusiveMaximum != null ? '<' : '≤';
  if (min != null && max != null) {
    resp += `Must be ${minOp} ${min} and ${maxOp} ${max}. `;
  } else if (min != null) {
    resp += `Must be ${minOp} ${min}. `;
  } else if (max != null) {
    resp += `Must be ${maxOp} ${max}. `;
  }

  return resp;
}

export function getArrayConstraintText(property: Property) {
  let resp = '';
  const min = property.minItems;
  const max = property.maxItems;

  if (min != null && max != null) {
    if (min === max) {
      resp += `Select exactly ${maybePluralize(min, 'item')}. `;
    } else {
      resp += `Select between ${min} and ${max} ${maybePluralizeWord(max, 'item')}. `;
    }
  } else if (min != null) {
    resp += `Select at least ${maybePluralize(min, 'item')}. `;
  } else if (max != null) {
    resp += `Select at most ${maybePluralize(max, 'item')}. `;
  }
  return resp;
}

export function getProjectOptions(
  data: ViewerTeamsProjectsQuery
): ProjectOption[] {
  const projectOptions: ProjectOption[] = [];

  const teams = [...(data.viewer?.teams?.edges ?? [])].filter(
    teamEdge => teamEdge.node
  );

  teams.forEach(teamEdge => {
    const team = teamEdge.node!;

    const projects = [...(team.projects?.edges ?? [])].filter(
      projectEdge => projectEdge.node
    );

    projects.forEach(projectEdge => {
      const project = projectEdge.node!;
      projectOptions.push({
        entityName: team.name,
        projectName: project.name,
        label: `${project.name}`,
        value: `${team.name}/${project.name}`,
      });
    });
  });

  // sort all projects alphabetically by project name, then by entity name
  return projectOptions.sort((a, b) => {
    const projectComparison = a.projectName.localeCompare(b.projectName);
    if (projectComparison !== 0) {
      return projectComparison;
    }
    return a.entityName.localeCompare(b.entityName);
  });
}

export function getArtifactOptions(
  data: ArtifactPathSearchArtifactsQuery
): ArtifactOption[] {
  const artifactOptions: ArtifactOption[] = [];

  const artifactCollections =
    data.entity?.project?.artifactCollectionsSearch?.edges ?? [];

  artifactCollections.forEach(collectionEdge => {
    const collection = collectionEdge.node;
    if (collection) {
      const aliases: AliasOrVersionOption[] = [];
      const versions: AliasOrVersionOption[] = [];

      const versionSet = new Set<string>();
      const aliasSet = new Set<string>();

      collection.artifacts?.edges?.forEach(artifactEdge => {
        const artifact = artifactEdge.node;
        if (
          artifact &&
          artifact.versionIndex !== undefined &&
          artifact.versionIndex !== null
        ) {
          versionSet.add(`v${artifact.versionIndex}`);
        }
      });

      collection.artifacts?.edges?.forEach(artifactEdge => {
        const artifact = artifactEdge.node;
        if (artifact) {
          if (artifact.aliases) {
            artifact.aliases.forEach(alias => {
              // dedupe versions from aliases and dedupe duplicate aliases
              if (
                alias &&
                !versionSet.has(alias.alias) &&
                !aliasSet.has(alias.alias)
              ) {
                aliasSet.add(alias.alias);
                aliases.push({
                  id: alias.id,
                  alias: alias.alias,
                  label: alias.alias,
                  value: alias.alias,
                  type: 'alias',
                });
              }
            });
          }

          if (
            artifact.versionIndex !== undefined &&
            artifact.versionIndex !== null
          ) {
            versions.push({
              id: artifact.id,
              alias: `v${artifact.versionIndex}`,
              label: `v${artifact.versionIndex}`,
              value: `v${artifact.versionIndex}`,
              type: 'version',
            });
          }
        }
      });

      aliases.sort((a, b) => a.alias.localeCompare(b.alias));
      versions.sort((a, b) => {
        const aNum = parseInt(a.alias.replace('v', ''), 10);
        const bNum = parseInt(b.alias.replace('v', ''), 10);
        return bNum - aNum;
      });

      const groupedAliasesOrVersions: GroupedAliasOrVersionOptions[] = [];
      if (aliases.length > 0) {
        groupedAliasesOrVersions.push({
          label: 'Aliases',
          options: aliases,
        });
      }
      if (versions.length > 0) {
        groupedAliasesOrVersions.push({
          label: 'Versions',
          options: versions,
        });
      }

      artifactOptions.push({
        name: collection.name,
        label: collection.name,
        value: collection.name,
        aliasesOrVersions: groupedAliasesOrVersions,
      });
    }
  });

  return artifactOptions.sort((a, b) => a.name.localeCompare(b.name));
}
